import React, { Children } from 'react';
import { useAutoScroll } from '@hooks/useAutoScroll';
import { Box, Stack } from '@mui/material';

import type { ScrollContainerProps } from './ScrollContainer.types';

/**
 * Scrollable container for chat messages with auto-scroll behavior
 * Displays empty state when no children are present
 *
 * @example
 * ```tsx
 * <ScrollContainer
 *   emptyState={<EmptyState title="No messages" description="Start chatting!" />}
 * >
 *   {messages.map(msg => <MessageBubble key={msg.id} {...msg} />)}
 * </ScrollContainer>
 * ```
 */
export const ScrollContainer: React.FC<ScrollContainerProps> = ({
  children,
  autoScroll = true,
  emptyState,
  scrollBehavior = 'smooth',
  onScrollToTop,
  className,
}) => {
  // Use children count instead of children reference to prevent excessive re-renders
  const childrenCount = Children.count(children);

  const { messagesEndRef, messagesContainerRef } = useAutoScroll([childrenCount], {
    behavior: scrollBehavior,
    enabled: autoScroll,
  });

  // Check if children is empty
  const hasChildren = childrenCount > 0;

  // Handle scroll to top detection
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    if (element.scrollTop === 0 && onScrollToTop) {
      onScrollToTop();
    }
  };

  return (
    <Box
      ref={messagesContainerRef}
      className={className}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
      onScroll={handleScroll}
    >
      {/* Empty state */}
      {!hasChildren && emptyState}

      {/* Messages */}
      {hasChildren && (
        <Stack spacing={2} sx={{ display: 'flex' }}>
          {children}
          <div ref={messagesEndRef} />
        </Stack>
      )}
    </Box>
  );
};
